import { useState, useEffect } from "react";
import { Home as HomeIcon, Users, User, Bell } from "lucide-react";
import { toast } from "sonner";
import { C } from "./data";
import Auth from "./Auth";
import Home from "./Home";
import Emergency from "./Emergency";
import Profile from "./Profile";
import Dependents from "./Dependents";
import SelfTest from "./SelfTest";
import Notices from "./Notices";
import FamilyLocate from "./FamilyLocate";
import Subscribe from "./Subscribe";
import DebitOrderForm from "./DebitOrderForm";
import CoachMarks from "./CoachMarks";
import DesktopGate from "./DesktopGate";
import InstallPrompt from "./InstallPrompt";
import { isMobileDevice } from "./device";
import { hasSession, fetchProfile, logoutConsumer, pushLocation, setLocationSharing, requestUpgrade, getMySubscription, getPlans, getMyDebitOrder, signMyDebitOrder, getInvitePrefill } from "./consumerAuth";

const coachKey = (id) => `rq_coach_done_${id}`;
const qp = () => new URLSearchParams(window.location.search);

const NAV = [
  { id: "home", label: "Home", icon: HomeIcon },
  { id: "dependents", label: "Household", icon: Users },
  { id: "notices", label: "Alerts", icon: Bell },
  { id: "profile", label: "Profile", icon: User },
];

export default function ConsumerApp() {
  const [screen, setScreen] = useState(() => (hasSession() ? "restoring" : "auth"));
  const [session, setSession] = useState(null);
  const [showCoach, setShowCoach] = useState(false);
  // Each internal screen gets its own history entry so the phone's Back button walks the app
  // instead of leaving it. Landing on a foreign entry re-anchors on Home (never exits).
  const nav = (s) => {
    setScreen(s);
    if (s !== "auth") { try { window.history.pushState({ rqScreen: s }, ""); } catch (e) { /* history unavailable */ } }
  };
  useEffect(() => {
    try { window.history.replaceState({ rqScreen: "home" }, ""); } catch (e) { /* history unavailable */ }
    const onPop = (e) => {
      const s = e.state?.rqScreen;
      if (s && s !== "home") { setScreen(s); return; }
      setScreen("home");
      try { window.history.pushState({ rqScreen: "home" }, ""); } catch (err) { /* history unavailable */ }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Restore the 10-year session on every app open. NEVER drop the token on a transient/offline
  // error — only a genuine auth rejection (401/403) signs the subscriber out.
  const restoreSession = async (attempt = 0) => {
    if (!hasSession()) { setScreen("auth"); return; }
    try {
      const s = await fetchProfile();
      // An activation link names ONE person. If this phone still holds someone else's session (e.g. a
      // family member's), never drop the invitee into that account — sign it out and show activation.
      const ci = qp().get("ci");
      if (ci || qp().get("invite")) {
        let mine = false;
        if (ci) { try { const inv = await getInvitePrefill(ci); mine = inv?.id && inv.id === s?.user?.contact_id; } catch (e) { /* invalid link — fall through */ } }
        if (!mine) { logoutConsumer(); setSession(null); setScreen("auth"); return; }
      }
      setSession(s);
      setScreen("home");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 403) {
        logoutConsumer(); setSession(null); setScreen("auth");
        if (code === 403) toast.error("That login isn't a member account", { description: "It belongs to the ResQconnect staff console. Sign in with your ResQ1 member email." });
        return;
      }
      if (attempt < 3) { setTimeout(() => restoreSession(attempt + 1), 1500); return; }
      setScreen("reconnect");
    }
  };
  useEffect(() => { restoreSession(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onAuthed = async () => {
    try {
      const p = await fetchProfile();
      setSession(p);
      if (p?.user?.id) {
        // Don't teach "hold to panic" to a member whose panic button is locked — the overlay also
        // sits over the subscribe CTA.
        const covered = !!(p?.cover?.security_active || p?.user?.security_active);
        try { if (covered && !localStorage.getItem(coachKey(p.user.id))) setShowCoach(true); } catch (e) { /* storage unavailable */ }
      }
    } catch (e) { console.error("Profile fetch failed after auth", e); }
    nav("home");
  };
  const finishCoach = () => {
    if (session?.user?.id) { try { localStorage.setItem(coachKey(session.user.id), "1"); } catch (e) { /* storage unavailable */ } }
    setShowCoach(false);
  };
  const replayTour = () => { nav("home"); setShowCoach(true); };
  const onSignOut = () => { logoutConsumer(); setSession(null); nav("auth"); };
  const onToggleShare = async (enabled) => {
    setSession((s) => (s ? { ...s, user: { ...s.user, share_location: enabled } } : s));
    try {
      await setLocationSharing(enabled);
    } catch (e) {
      setSession((s) => (s ? { ...s, user: { ...s.user, share_location: !enabled } } : s));
      toast.error("Couldn't update location sharing");
    }
  };
  // Live-location relay: while sharing is ON and the app is open, push position (foreground only — PWA cannot track in background).
  useEffect(() => {
    if (!session?.user?.share_location || !navigator.geolocation) return;
    let last = 0;
    const id = navigator.geolocation.watchPosition(
      (p) => {
        const now = Date.now();
        if (now - last < 15000) return;
        last = now;
        pushLocation({ lat: +p.coords.latitude.toFixed(6), lng: +p.coords.longitude.toFixed(6), accuracy: Math.round(p.coords.accuracy || 0) }).catch((e) => console.debug("location push skipped", e));
      },
      () => {}, { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [session?.user?.share_location]); // eslint-disable-line react-hooks/exhaustive-deps
  const showNav = ["home", "dependents", "profile", "notices"].includes(screen);
  const securityActive = !!(session?.cover?.security_active || session?.user?.security_active);
  const [sub, setSub] = useState(null);
  const [fromPrice, setFromPrice] = useState(null);
  // Free members only: the subscribe prompt never renders for anyone already covered.
  useEffect(() => {
    if (!session?.user || securityActive) return;
    getMySubscription().then((d) => setSub(d.subscription)).catch(() => {});
    getPlans().then((d) => {
      if (d.enabled && (d.plans || []).length) setFromPrice(Math.min(...d.plans.map((p) => p.price)));
    }).catch(() => {});
  }, [session?.user?.id, securityActive]); // eslint-disable-line react-hooks/exhaustive-deps
  const onUpgrade = async () => {
    try {
      await requestUpgrade();
      toast.success("Upgrade request sent", { description: "Our team will contact you to activate the Security Package." });
    } catch (e) {
      toast.error("Couldn't send upgrade request", { description: e.response?.data?.detail || "Please try again." });
    }
  };

  // Debit-order authority: show the banner/prompt only when the admin has requested one and it's unsigned.
  const [doInfo, setDoInfo] = useState(null);
  useEffect(() => {
    if (!session?.user) return;
    getMyDebitOrder().then(setDoInfo).catch(() => setDoInfo(null));
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const doNeeded = !!(doInfo?.requested && !doInfo?.signed);
  const submitDebitOrder = async (f) => {
    await signMyDebitOrder(f);
    setDoInfo((d) => ({ ...(d || {}), signed: true }));
    toast.success("Debit order received", { description: "Thank you — your cover continues uninterrupted." });
    nav("home");
  };

  // Gate: /app is a mobile PWA — block desktop signups, guide users to install on their phone.
  // Bypass with ?desktop=1 for previewing on a laptop.
  if (!isMobileDevice() && qp().get("desktop") !== "1") return <DesktopGate />;

  return (
    <div style={{ minHeight: "100dvh", background: `linear-gradient(160deg,#0d1826 0%,#0B1E3B 55%,#12263f 100%)`, fontFamily: "'Inter', system-ui, sans-serif", display: "flex", justifyContent: "center" }} data-testid="consumer-app">
      {/* Real PWA viewport: full-bleed on mobile, centered app column on desktop */}
      <div style={{ position: "relative", width: "100%", maxWidth: 480, background: C.body, minHeight: "100dvh", display: "flex", flexDirection: "column", boxShadow: "0 0 80px rgba(0,0,0,0.35)" }}>
        {/* content */}
        <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          {screen === "auth" && (
            <>
              {qp().get("install") === "1" && (
                <div style={{ padding: "20px 20px 0" }} data-testid="auth-install-banner"><InstallPrompt /></div>
              )}
              <Auth onAuth={onAuthed} />
            </>
          )}
          {screen === "restoring" && <SessionSplash label="Signing you in…" />}
          {screen === "reconnect" && <SessionSplash label="Can't reach the network right now — you're still signed in." onRetry={() => { setScreen("restoring"); restoreSession(); }} />}
          {screen === "home" && <Home nav={nav} onPanic={() => nav("emergency")} user={session?.user} securityActive={securityActive} onUpgrade={onUpgrade} onSubscribe={() => nav("subscribe")} subscription={sub} fromPrice={fromPrice} debitOrderNeeded={doNeeded} onDebitOrder={() => nav("debitorder")} />}
          {screen === "subscribe" && <Subscribe nav={nav} user={session?.user} onDone={(s) => { setSub(s); nav("home"); }} />}
          {screen === "debitorder" && (
            <div style={{ padding: "18px 18px 40px" }} data-testid="consumer-debitorder">
              <button onClick={() => nav("home")} data-testid="do-back" style={{ background: "none", border: 0, color: C.slate, fontSize: 13, marginBottom: 14, cursor: "pointer", padding: 0 }}>← Back</button>
              <h1 style={{ fontSize: 20, fontWeight: 800, color: C.ink, margin: "0 0 4px" }}>Confirm your debit order</h1>
              <p style={{ fontSize: 13, color: C.slate, margin: "0 0 16px", lineHeight: 1.55 }}>Your ResQ1 cover continues directly with us. Please confirm your debit order authority — processed by Acuideas on the 1st of each month. Your cover stays active throughout.</p>
              <DebitOrderForm
                initial={{ name: (doInfo?.full_name || session?.user?.name || "").split(" ")[0], surname: (doInfo?.full_name || session?.user?.name || "").split(" ").slice(1).join(" "), mobile: doInfo?.cell || session?.user?.cell, id_number: doInfo?.id_number, plan_code: doInfo?.plan }}
                plans={doInfo?.plans || []}
                submitLabel="Sign and submit" onSubmit={submitDebitOrder} />
            </div>
          )}
          {screen === "emergency" && <Emergency onCancel={() => nav("home")} />}
          {screen === "profile" && <Profile user={session?.user} cover={session?.cover} onSignOut={onSignOut} onReplayTour={replayTour} nav={nav} />}
          {screen === "dependents" && <Dependents nav={nav} />}
          {screen === "selftest" && <SelfTest nav={nav} />}
          {screen === "notices" && <Notices />}
          {screen === "locate" && <FamilyLocate nav={nav} user={session?.user} onToggleShare={onToggleShare} />}
        </div>

        {/* bottom nav */}
        {showNav && (
          <div style={{ display: "flex", background: "#fff", borderTop: "1px solid rgba(26,30,36,0.08)", flexShrink: 0, paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="consumer-bottomnav">
            {NAV.map((t) => {
              const on = screen === t.id; const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => nav(t.id)} data-testid={`nav-${t.id}`}
                  style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "11px 0 14px", background: "none", border: 0, cursor: "pointer", color: on ? C.red : C.slate }}>
                  <Icon size={20} /> <span style={{ fontSize: 10, fontWeight: 600 }}>{t.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {showCoach && session && screen !== "auth" && screen !== "emergency" && <CoachMarks onDone={finishCoach} />}
      </div>
    </div>
  );
}

function SessionSplash({ label, onRetry }) {
  return (
    <div data-testid="session-splash" style={{ minHeight: "70dvh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: 32, textAlign: "center" }}>
      <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 30, fontWeight: 700, color: C.navy, letterSpacing: "-0.02em" }}>ResQ1</div>
      <div style={{ fontSize: 14, color: C.slate, maxWidth: 300, lineHeight: 1.5 }}>{label}</div>
      {onRetry && (
        <button onClick={onRetry} data-testid="session-retry"
          style={{ marginTop: 8, padding: "11px 24px", background: C.red, color: "#fff", border: 0, borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Retry</button>
      )}
    </div>
  );
}
